import { useEffect, useState } from 'react';
import { NARROW_TABLE_VIEW_MEDIA_QUERY } from 'utils/tableViewWidth';

/**
 * True when the viewport only fits ONE table (≤ SINGLE_TABLE_VIEW_MAX_PX) —
 * the width at which the table view becomes the phone experience on every
 * device: navigation strip with one fan per open table, swipe/fan switching,
 * no grid entry point.
 *
 * Width, not pointer type: `useIsMobile` / `useSupportsHover` are
 * `(hover: hover)` queries, so a narrowed DESKTOP window still reads as
 * "desktop" and was getting the spread-fan toggle header — the mobile/desktop
 * split Jason flagged on 2026-08-15. Live media-query listener, same idiom as
 * the bet tray's width variant (RaiseActionExpanded).
 */
export function useIsNarrowTableView(): boolean {
  const [isNarrow, setIsNarrow] = useState(
    () =>
      typeof window !== 'undefined' &&
      (window.matchMedia?.(NARROW_TABLE_VIEW_MEDIA_QUERY)?.matches ?? false)
  );

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mql = window.matchMedia(NARROW_TABLE_VIEW_MEDIA_QUERY);
    const onChange = () => setIsNarrow(mql.matches);
    if (mql.addEventListener) {
      mql.addEventListener('change', onChange);
      return () => mql.removeEventListener('change', onChange);
    }
    mql.addListener(onChange);
    return () => mql.removeListener(onChange);
  }, []);

  return isNarrow;
}
